export const inviteesEmptyStateText = "No one has been invited yet.";

export const formatInviteeName = (name: string | null): string => name ?? "(no name)";

export interface InviteePickerNotice {
    type: "success" | "info" | "error";
    text: string;
}

export const guestListChangeFailedNotice = (): InviteePickerNotice => ({
    type: "error",
    text: "Could not update the guest list. Please try again.",
});

export const bulkAddFailedNotice = (): InviteePickerNotice => ({
    type: "error",
    text: "Those guests could not be added. Please try again.",
});

export const bulkAddedNotice = (
    added: number,
    requested: number,
): InviteePickerNotice => {
    if (requested === 0) {
        return { type: "info", text: "No contacts matched your search." };
    }
    if (added === 0) {
        return { type: "info", text: "Everyone matching is already on the guest list." };
    }
    const noun = added === 1 ? "guest" : "guests";
    const skipped = requested - added;
    if (skipped > 0) {
        const skippedNoun = skipped === 1 ? "was" : "were";
        return {
            type: "success",
            text: `Added ${added} ${noun}. ${skipped} ${skippedNoun} already on the guest list.`,
        };
    }
    return { type: "success", text: `Added ${added} ${noun} to the guest list.` };
};

export interface InviteeSummaryCounts {
    total: number;
    yes: number;
    no: number;
    maybe: number;
    notYetInvited: number;
    yesHeads: number;
}

export const rosterSummaryCounts = (
    invitees: {
        rsvpResponse?: string | null;
        invitedAt?: string | null;
        additionalGuests: number;
    }[],
): InviteeSummaryCounts => {
    const yesInvitees = invitees.filter(invitee => invitee.rsvpResponse === "yes");
    return {
        total: invitees.length,
        yes: yesInvitees.length,
        no: invitees.filter(invitee => invitee.rsvpResponse === "no").length,
        maybe: invitees.filter(invitee => invitee.rsvpResponse === "maybe").length,
        notYetInvited: invitees.filter(invitee => !invitee.invitedAt).length,
        yesHeads: yesInvitees.reduce(
            (heads, invitee) => heads + invitee.additionalGuests,
            yesInvitees.length,
        ),
    };
};

export const inviteeSummaryText = (counts: InviteeSummaryCounts): string => {
    if (counts.total === 0) {
        return "No invitees yet";
    }
    const noun = counts.total === 1 ? "invitee" : "invitees";
    const parts = [`${counts.total} ${noun}`];
    if (counts.yes > 0) {
        parts.push(`${counts.yes} yes`);
    }
    if (counts.yesHeads > counts.yes) {
        parts.push(`${counts.yesHeads} people`);
    }
    if (counts.maybe > 0) {
        parts.push(`${counts.maybe} maybe`);
    }
    if (counts.no > 0) {
        parts.push(`${counts.no} no`);
    }
    if (counts.notYetInvited > 0) {
        parts.push(`${counts.notYetInvited} not yet invited`);
    }
    return parts.join(" · ");
};

export const inviteFlowExplainerText =
    "Adding guests doesn't email them — use Send email invitations, or copy a guest's RSVP link and mark them invited yourself.";

export const additionalGuestsLabel = (count: number): string =>
    count === 0 ? "Just me" : `+${count} ${count === 1 ? "guest" : "guests"}`;

export const invitationResentText = (name: string | null): string =>
    `Invitation resent to ${formatInviteeName(name)}.`;

export const bouncedExplainerText =
    "This address bounced — copy their RSVP link and send it another way, or fix their email.";

export const invitationResultText = (result: {
    queued: number;
    skipped: number;
}): string => {
    const noun = result.queued === 1 ? "invitation is" : "invitations are";
    const sent = `${result.queued} ${noun} on the way.`;
    if (result.skipped === 0) {
        return sent;
    }
    const skippedNoun = result.skipped === 1 ? "guest has" : "guests have";
    return `${sent} ${result.skipped} ${skippedNoun} no email address — copy their RSVP link instead.`;
};
